import React, { useState, useRef, useEffect } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUser } from "../../../Context/UserContext";
import { useCart } from "../../../Context/CartContext";
import LanguageSwitcher from "../../UI/LanguageSwitcher/LanguageSwitcher";
import ThemeToggle from "../../UI/ThemeToggle/ThemeToggle";
import Logo from "../Logo/Logo";
import "./Header.css";

const Header = () => {
  const { t } = useTranslation('common');
  const { isAuthenticated, user, logout, canUseCart } = useUser();
  const { itemCount } = useCart();
  const navigate = useNavigate();

  // 🔽 Menu profil (regroupe Mon profil, Mes voyages, Déconnexion sous la pastille avatar).
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // Ferme le menu profil au clic extérieur / touche Échap.
  useEffect(() => {
    if (!profileOpen) return;
    const onClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setProfileOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [profileOpen]);

  const closeMenu = () => {
    const toggle = document.getElementById("nav-toggle");
    if (toggle) toggle.checked = false;
    setProfileOpen(false);
  };

  const handleLogout = () => {
    logout();
    closeMenu();
    navigate("/");
  };

  const navLinkClass = ({ isActive }) =>
    isActive ? "nav-link active" : "nav-link";

  return (
    <header className="header">
      <div className="header-container">

        {/* LOGO */}
        <Link to="/" className="logo" onClick={closeMenu} aria-label={t('ui.homeAria')}>
          <Logo variant="full" theme="dark" height={52} />
        </Link>

        {/* BURGER TOGGLE */}
        <input type="checkbox" id="nav-toggle" className="nav-toggle" />
        <label htmlFor="nav-toggle" className="nav-toggle-label" aria-label={t('nav.toggleMenu')}>
          <span></span>
        </label>

        {/* OVERLAY MOBILE */}
        <label htmlFor="nav-toggle" className="nav-overlay" />

        {/* NAVIGATION — « Accueil » (→ /) et « Trajets » (→ /travels) : deux repères familiers.
            L'accueil EST la vue Trajets ; les deux mènent à la recherche, mais l'utilisateur
            retrouve « Accueil » là où il l'attend. */}
        <nav className="nav-links" role="navigation">
          <NavLink to="/" end className={navLinkClass} onClick={closeMenu}>
            {t('nav.home')}
          </NavLink>
          <NavLink to="/travels" className={navLinkClass} onClick={closeMenu}>
            {t('nav.trips')}
          </NavLink>
          <NavLink to="/contact" className={navLinkClass} onClick={closeMenu}>
            {t('nav.contact')}
          </NavLink>

          <div className="nav-divider" />

          {isAuthenticated ? (
            <>
              {/* 🛒 Panier — condition centralisée canUseCart (jamais un invité) */}
              {canUseCart && (
                <NavLink
                  to="/user/panier"
                  className={({ isActive }) => isActive ? "nav-link nav-cart active" : "nav-link nav-cart"}
                  onClick={closeMenu}
                  title={t('nav.cart')}
                >
                  🛒
                  {itemCount > 0 && <span className="cart-badge">{itemCount}</span>}
                </NavLink>
              )}

              {/* 👤 Pastille avatar → menu déroulant (Mon profil, Mes voyages, Déconnexion) */}
              <div className="profile-menu" ref={profileRef}>
                <button
                  type="button"
                  className={profileOpen ? "profile-trigger open" : "profile-trigger"}
                  onClick={() => setProfileOpen(o => !o)}
                  aria-haspopup="menu"
                  aria-expanded={profileOpen}
                >
                  <span className="avatar-initials">
                    {user?.prenom ? user.prenom[0].toUpperCase() : "?"}
                  </span>
                  <span className="profile-name">{user?.prenom}</span>
                  <span className="profile-caret" aria-hidden="true">▾</span>
                </button>

                {profileOpen && (
                  <div className="profile-dropdown" role="menu">
                    <NavLink to="/user/profile" className="profile-item" role="menuitem" onClick={closeMenu}>
                      {t('nav.profile')}
                    </NavLink>
                    <NavLink to="/user/reservations" className="profile-item" role="menuitem" onClick={closeMenu}>
                      {t('nav.myTrips')}
                    </NavLink>
                    <div className="profile-sep" />
                    <button type="button" className="profile-item profile-item--logout" role="menuitem" onClick={handleLogout}>
                      {t('nav.logout')}
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <NavLink
              to="/login"
              className={({ isActive }) => isActive ? "nav-link nav-btn nav-btn--login active" : "nav-link nav-btn nav-btn--login"}
              onClick={closeMenu}
            >
              {t('nav.login')}
            </NavLink>
          )}

          {/* Utilitaires discrets : langue + thème, regroupés à droite */}
          <div className="nav-utils">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </nav>

      </div>
    </header>
  );
};

export default Header;
